/**
 * AudiencesPanel (MKT-REDESIGN-01 M2) — the saved audiences on the Marketing
 * page: count as of last check, dynamic/static, Use in a campaign (jumps to
 * the template picker with the audience preselected), Edit, Duplicate,
 * Archive. Starter audiences below for the ones every shop runs.
 */
import { useState } from "react";
import { Archive, Copy, MessageSquare, Pencil, Plus, Send, Users } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { AudienceBuilder } from "./AudienceBuilder";
import { describeSpec, STARTER_AUDIENCES, useArchiveAudience, useAudiences, type AudienceSpec, type SavedAudience } from "./audiences";

export interface AudiencePick { label: string; spec: AudienceSpec; audienceId?: string | null }

export function AudiencesPanel({ onUse, onText, openNew, onOpenedNew }: {
  /** "Use in a campaign" — the page opens the template picker with this audience. */
  onUse: (pick: AudiencePick) => void;
  /** M6: "Text this audience" — the page opens the text campaign form with it. */
  onText?: (pick: AudiencePick) => void;
  /** A spec handed in from elsewhere (the All Customers report) to open the builder with. */
  openNew?: { name?: string; spec: AudienceSpec } | null;
  onOpenedNew?: () => void;
}) {
  const { data: audiences, isLoading } = useAudiences();
  const archive = useArchiveAudience();
  const canSend = useCan("marketing.send");
  const [editing, setEditing] = useState<{ id?: string; name?: string; spec?: AudienceSpec; mode?: "dynamic" | "static" } | null>(null);
  const [showStarters, setShowStarters] = useState((audiences?.length ?? 0) === 0);
  const list = audiences ?? [];
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null);
  const open = openNew ? { name: openNew.name, spec: openNew.spec } : editing;

  return (
    <section className="mb-6">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900">
          <Users className="h-4 w-4 text-brand-600" /> Audiences
          <InfoTip label="About audiences">Who a campaign goes to, picked by plan status, service history, equipment, location and value. An audience that keeps up to date is re-checked each time you use it.</InfoTip>
        </h2>
        <button type="button" onClick={() => setEditing({})} className="ml-auto inline-flex h-8 items-center gap-1 rounded-lg bg-brand-700 px-3 text-xs font-semibold text-white hover:bg-brand-800">
          <Plus className="h-3.5 w-3.5" /> New audience
        </button>
      </div>

      {isLoading ? (
        <Card className="h-20 animate-pulse bg-slate-100" />
      ) : list.length === 0 ? (
        <Card className="p-5 text-center text-sm text-slate-500">No saved audiences yet — start from one below, or build your own.</Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {list.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{a.name}</span>
                    <span className={cn("rounded-full px-1.5 py-px text-[10px] font-semibold", a.mode === "dynamic" ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200" : "bg-slate-100 text-slate-500")}>{a.mode === "dynamic" ? "keeps up to date" : `frozen · ${a.static_custnos?.length ?? 0}`}</span>
                  </div>
                  <div className="truncate text-[11.5px] text-slate-500">{a.description ?? describeSpec(a.spec)}</div>
                </div>
                <div className="shrink-0 text-right text-xs">
                  <div className="text-base font-bold tabular-nums text-slate-800">{a.last_sendable != null ? a.last_sendable.toLocaleString() : "—"}</div>
                  <div className="text-[10.5px] text-slate-400">{a.last_counted_at ? `reachable · ${fmt(a.last_counted_at)}` : "not counted yet"}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => onUse({ label: a.name, spec: a.mode === "static" && a.static_custnos?.length ? { custnos: a.static_custnos, active_only: false } : a.spec, audienceId: a.id })}
                    className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-brand-800"><Send className="h-3 w-3" /> Use in a campaign</button>
                  {onText && <IconBtn title="Text this audience (opted-in customers only)" onClick={() => onText({ label: a.name, spec: a.mode === "static" && a.static_custnos?.length ? { custnos: a.static_custnos, active_only: false } : a.spec, audienceId: a.id })}><MessageSquare className="h-3.5 w-3.5" /></IconBtn>}
                  <IconBtn title="Edit" onClick={() => setEditing({ id: a.id, name: a.name, spec: a.spec, mode: a.mode })}><Pencil className="h-3.5 w-3.5" /></IconBtn>
                  <IconBtn title="Duplicate" onClick={() => setEditing({ name: `${a.name} (copy)`, spec: a.spec, mode: a.mode })}><Copy className="h-3.5 w-3.5" /></IconBtn>
                  {canSend && <IconBtn title="Archive" onClick={() => archive.mutate(a.id)} danger><Archive className="h-3.5 w-3.5" /></IconBtn>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <button type="button" onClick={() => setShowStarters((v) => !v)} className="mt-2 text-xs font-medium text-slate-500 hover:text-slate-800">
        {showStarters ? "Hide" : "Show"} starter audiences ({STARTER_AUDIENCES.length})
      </button>
      {showStarters && (
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {STARTER_AUDIENCES.map((s) => (
            <div key={s.key} className="flex flex-col rounded-xl border border-dashed border-slate-200 bg-white p-3">
              <div className="text-sm font-semibold text-slate-800">{s.name}</div>
              <div className="mt-0.5 flex-1 text-[11.5px] text-slate-500">{s.description}</div>
              <div className="mt-2 flex items-center gap-1.5">
                <button type="button" onClick={() => onUse({ label: s.name, spec: s.spec })} className="rounded-md bg-brand-50 px-2 py-1 text-[11px] font-semibold text-brand-700 hover:bg-brand-100">Use</button>
                {onText && <button type="button" onClick={() => onText({ label: s.name, spec: s.spec })} title="Text this audience (opted-in customers only)" className="rounded-md bg-violet-50 px-2 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-100">Text</button>}
                <button type="button" onClick={() => setEditing({ name: s.name, spec: s.spec, mode: "dynamic" })} className="rounded-md border border-hairline px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50">Save as mine</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <AudienceBuilder initial={open} onClose={() => { setEditing(null); onOpenedNew?.(); }} onSaved={() => { setEditing(null); onOpenedNew?.(); }} />
      )}
    </section>
  );
}

function IconBtn({ title, onClick, children, danger }: { title: string; onClick: () => void; children: React.ReactNode; danger?: boolean }) {
  return (
    <button type="button" title={title} onClick={onClick} className={cn("rounded-md border border-hairline bg-white p-1.5 text-slate-500 hover:bg-slate-50", danger && "hover:text-rose-600")}>{children}</button>
  );
}

export type { SavedAudience };
